// src/oura/merchant/PhoneFold.tsx
//
// A merchant card that folds by its head on a phone and tablet (under
// 1024px), and never on a wide screen (27 Oct 2026). Jack: Our work on a
// phone ran to five screens, so its cards fold there, each saying what it
// holds while folded. On a wide screen there is room for all of it, and the
// merchant shell's rule (NoFold) still holds: nothing hides what they came for.
//
// Not remembered: a phone visit starts from the same folds every time.

import { ChevronDownIcon } from "lucide-react";
import { useEffect, useId, useState, type ReactNode } from "react";
import { FOCUS_RING, NARROW_VIEWPORT, SURFACE, TYPE, useMediaQuery } from "@oura/ui";
import { cn } from "../lib/cn";

export function PhoneFoldCard({
    title,
    right,
    foldedFact,
    foldedOnPhone = false,
    openWhen = false,
    anchor,
    className,
    children,
}: {
    title: ReactNode;
    /** Beside the title, open or folded (a link, a button). */
    right?: ReactNode;
    /** Said in the head while folded, so a folded card still says what it holds. */
    foldedFact?: ReactNode;
    /** Starts folded on a phone. */
    foldedOnPhone?: boolean;
    /**
     * Unfolds the card each time this turns true: something the card's own head
     * started, such as a form opening inside it, must not open into a folded
     * card. They can fold it again. The fold is read once, so a changed
     * `foldedOnPhone` alone would not do this (#4748).
     */
    openWhen?: boolean;
    /** DOM id, for in-page anchors such as `#plan`. */
    anchor?: string;
    className?: string;
    children: ReactNode;
}) {
    const narrow = useMediaQuery(NARROW_VIEWPORT);
    const [folded, setFolded] = useState(foldedOnPhone);
    useEffect(() => {
        if (openWhen) setFolded(false);
    }, [openWhen]);
    const headId = useId();
    const bodyId = useId();
    const shut = narrow && folded;
    return (
        <section id={anchor} data-anchor={anchor} aria-labelledby={headId} className={cn(SURFACE.card, "flex min-w-0 flex-col gap-4 p-5 sm:p-6", shut && "gap-0", className)}>
            <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
                {narrow ? (
                    <h2 id={headId} className={cn(TYPE.tt, "m-0 min-w-0 flex-1 text-gray-900")}>
                        <button
                            type="button"
                            aria-expanded={!folded}
                            aria-controls={bodyId}
                            onClick={() => setFolded((f) => !f)}
                            className={cn("-my-2 flex min-h-[44px] w-full min-w-0 items-center gap-2 rounded py-2 text-left", FOCUS_RING)}
                        >
                            <span className="min-w-0">
                                <span className="block">{title}</span>
                                {shut && foldedFact && <span className={cn(TYPE.tc, "block font-normal text-gray-500")}>{foldedFact}</span>}
                            </span>
                            <ChevronDownIcon
                                aria-hidden
                                size={18}
                                className={cn("ml-auto shrink-0 text-gray-400 transition-transform duration-200 motion-reduce:transition-none", !folded && "rotate-180")}
                            />
                        </button>
                    </h2>
                ) : (
                    <h2 id={headId} className={cn(TYPE.tt, "m-0 text-gray-900")}>
                        {title}
                    </h2>
                )}
                {/* On a phone the head is the fold; its link or button opens with the body. */}
                {right && !narrow && right}
            </div>
            {/* Not rendered while folded: a `hidden` attribute loses to the flex class. */}
            {!shut && (
                <div id={bodyId} className="flex min-w-0 flex-col gap-4">
                    {right && narrow && <div className="flex flex-wrap items-center gap-3">{right}</div>}
                    {children}
                </div>
            )}
        </section>
    );
}
